/**
 * Schedule Fragment
 * Drives the schedule-delivery modal: it stamps the date input's selectable
 * range, keeps today's past time slots unchoosable, and converts the chosen
 * local date and time into the UTC value the form posts.
 *
 * The timezone work has to happen here — only the browser knows the user's
 * offset — which is also why the date bounds are stamped rather than rendered.
 */

import { Events } from '../../../../../../js/utils/events.js';

let initialized = false;

const DAY_MS = 86400000;

function ymd(date) {
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return date.getFullYear() + '-' + m + '-' + d;
}

function hm(date) {
    return String(date.getHours()).padStart(2, '0') + ':'
        + String(date.getMinutes()).padStart(2, '0');
}

function parts(form) {
    return {
        date: form.querySelector('[data-schedule-date]'),
        time: form.querySelector('[data-schedule-time]'),
        utc: form.querySelector('[data-schedule-utc]'),
        leadDays: Number(form.dataset.maxLeadDays),
    };
}

// ============================================================================
// Schedule Handlers
// Template contract:
//   <div data-schedule-modal>
//     <div data-schedule-error></div>
//     <form data-schedule-form data-max-lead-days="30">
//       <input type="hidden" data-schedule-utc name="scheduled_send_datetime">
//       <input type="date" data-schedule-date>
//       <select data-schedule-time>
// ============================================================================

const ScheduleHandlers = {
    /**
     * With today selected, only future slots are choosable; other days offer
     * all slots. Slot values are zero-padded HH:MM, so string compare works.
     */
    refreshTimeSlots(form) {
        const { date, time, leadDays } = parts(form);
        if (!date || !time) return;
        const now = new Date();
        const today = date.value === ymd(now);
        const nowHm = hm(now);
        // The lead is a rolling instant — now + leadDays * 24h, the same rule
        // the request layer enforces — not the end of that day. So the last
        // selectable date is only schedulable up to the current time of day,
        // and its later slots have to go the way today's earlier ones do.
        const cutoff = new Date(Date.now() + leadDays * DAY_MS);
        const lastDay = date.value === ymd(cutoff);
        const cutoffHm = hm(cutoff);
        let firstEnabled = null;
        for (let i = 0; i < time.options.length; i++) {
            const option = time.options[i];
            option.disabled = (today && option.value <= nowHm)
                || (lastDay && option.value > cutoffHm);
            if (!option.disabled && firstEnabled === null) firstEnabled = i;
        }
        if (time.options[time.selectedIndex].disabled && firstEnabled !== null) {
            time.selectedIndex = firstEnabled;
        }
    },

    /**
     * Today through today + max_lead_days, mirroring the server-side guards.
     * The server's "today" can differ from the browser's near midnight.
     */
    stampDateRange(form) {
        const { date, time, leadDays } = parts(form);
        if (!date || !time) return;
        // If today's last slot has passed, today is not schedulable at all.
        const now = new Date();
        const lastSlot = time.options[time.options.length - 1].value;
        const minDate = hm(now) >= lastSlot ? new Date(Date.now() + DAY_MS) : now;
        date.min = ymd(minDate);
        date.max = ymd(new Date(Date.now() + leadDays * DAY_MS));
        ScheduleHandlers.refreshTimeSlots(form);
    },

    /**
     * Convert the local pick to UTC, or stop the request and say why.
     *
     * htmx binds its own 'submit' listener directly on the form, and does not
     * check defaultPrevented. The old inline handler had to call
     * stopImmediatePropagation() because it ran on that same node. This one is
     * delegated in the capture phase from document, so stopping propagation
     * here means the event never reaches the form at all.
     */
    onSubmit(event, form) {
        const { date, time, utc, leadDays } = parts(form);
        const modal = form.closest('[data-schedule-modal]');
        const error = modal && modal.querySelector('[data-schedule-error]');

        const block = (message) => {
            if (error) {
                error.innerHTML =
                    '<div class="bg-red-50 border border-red-200 rounded-lg p-3" data-error-alert>'
                    + '<p class="text-sm text-red-800">' + message + '</p></div>';
            }
            event.preventDefault();
            event.stopPropagation();
        };

        if (!date || !time || !utc) return;
        if (!date.value || !time.value) {
            block('Pick a delivery date and time.');
            return;
        }
        // Interpreted in the browser's timezone, which is the point.
        const local = new Date(date.value + 'T' + time.value + ':00');
        if (isNaN(local.getTime()) || local <= new Date()) {
            block('Scheduled delivery time must be in the future.');
            return;
        }
        if (local > new Date(Date.now() + leadDays * DAY_MS)) {
            block('Scheduled delivery time cannot be more than ' + leadDays + ' days out.');
            return;
        }
        if (error) error.innerHTML = '';
        utc.value = local.toISOString();
    },
};

function activate(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[data-schedule-form]').forEach(ScheduleHandlers.stampDateRange);
}

// ============================================================================
// Initialization
// ============================================================================

export function initSchedule() {
    if (initialized) return;
    initialized = true;

    Events.on('submit', 'schedule-form', ScheduleHandlers.onSubmit);
    Events.on('change', 'schedule-date', (event, element) => {
        const form = element.closest('[data-schedule-form]');
        if (form) ScheduleHandlers.refreshTimeSlots(form);
    });

    activate(document);
    // The modal is swapped in on demand, so its range is stamped on arrival.
    document.addEventListener('htmx:afterSwap', (event) => activate(event.target));
}
